// Quote admin panel — mobile-first quote review with live email preview.
// Mounted by app.jsx when the path matches /admin/q/:token. Loads the booking
// record from Firestore via the loadBookingByToken CF (never from the token),
// lets Julie edit per-night rates + fees + discount, and on Approve + Send
// calls quoteApproveSend to write the sheet + email the guest.
import { useState, useEffect } from "preact/hooks";
import { loadBookingByToken, scrapeRoomRate, quoteApproveSend } from "../lib/api.js";
import {
  initBlankRates,
  computeQuote,
  buildPreviewText,
  formatMoney,
  formatOrdinalDate,
  isShortLead,
  isSelfBooking,
  SECOND_INSTALLMENT_DAYS,
} from "../lib/quote.js";
import { reportError } from "../lib/errlog.js";
import { isISODate, computeNights, inNightRange } from "../lib/validation.js";
import { MIN_NIGHTS, MAX_NIGHTS, PAYMENT_CHANNEL_LABELS } from "../config.js";

const CLEANING_DEFAULT = 899;
const WEDDING_FEE = 1500;
const DEPOSIT = 5000;

export function QuoteAdminPanel({ token, onDone }) {
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [rates, setRates] = useState([]); // [{date, rate}]
  const [cleaningFee, setCleaningFee] = useState(CLEANING_DEFAULT);
  const [wedding, setWedding] = useState("no");
  const [discountSubject, setDiscountSubject] = useState("");
  const [discountPct, setDiscountPct] = useState(0);
  const [paymentChannel, setPaymentChannel] = useState("check");
  // Card processing fee (session 47 — exact passthrough 3.3% + 30¢/payment):
  // default-ticked when the lead chose card; unticking waives the fee.
  const [cardFeeApplied, setCardFeeApplied] = useState(true);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [scrapeHint, setScrapeHint] = useState(null);

  // Load booking on mount.
  useEffect(() => {
    let alive = true;
    loadBookingByToken(token).then((b) => {
      if (!alive) return;
      if (!b) {
        setLoadError("Could not load this booking. The link may be invalid.");
        setLoading(false);
        return;
      }
      setBooking(b);
      setWedding(b.wedding || "no");
      setCleaningFee(Number(b.cleaning_fee) || CLEANING_DEFAULT);
      setPaymentChannel(b.payment_channel || "check");
      // Parse nightly_rates (JSON string) -> array. Fall back to blank rows
      // built from the booking's checkin/checkout dates.
      let parsed = [];
      try {
        parsed = b.nightly_rates ? JSON.parse(b.nightly_rates) : [];
      } catch {
        parsed = [];
      }
      if (Array.isArray(parsed) && parsed.length) {
        setRates(parsed.map((r) => ({ date: r.date, rate: r.rate == null ? "" : String(r.rate) })));
      } else if (b.checkin_date && b.checkout_date) {
        setRates(initBlankRates(b.checkin_date, b.checkout_date));
      }
      setLoading(false);
    }).catch((err) => {
      // Transient failure (offline / CF hiccup): api.loadBookingByToken throws
      // for non-4xx so we can say "retry", not "bad link" (session 34).
      if (!alive) return;
      reportError("quote_admin_load", err);
      setLoadError("Could not load this booking — check your connection and reload the page.");
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [token]);

  // Computed values (live). The fee is always a VISIBLE line (the "hide the
  // fee in the room rate" option was removed — transparent pricing only).
  const selfBooking = isSelfBooking(booking?.email);
  const q = computeQuote({
    rates,
    cleaningFee: Number(cleaningFee) || 0,
    wedding,
    discountPct: Number(discountPct) || 0,
    checkinIso: booking?.checkin_date,
    channel: booking?.channel,
    paymentChannel,
    cardFeeWaived: !cardFeeApplied,
    email: booking?.email,
  });
  // What the fee would be as a visible line (for the checkbox label only).
  const qFeeIfShown = paymentChannel === "card" && cardFeeApplied
    ? computeQuote({
        rates,
        cleaningFee: Number(cleaningFee) || 0,
        wedding,
        discountPct: Number(discountPct) || 0,
        checkinIso: booking?.checkin_date,
        channel: booking?.channel,
        paymentChannel,
        cardFeeWaived: false,
      })
    : null;
  const shortLead = q.shortLead;
  const reserveLink = booking ? `https://stay.onebeachroad.com/?r=${token}` : "";
  const previewText = buildPreviewText({
    booking,
    rates,
    cleaningFee: Number(cleaningFee) || 0,
    wedding,
    discountSubject,
    discountPct: Number(discountPct) || 0,
    paymentChannel,
    cardFeeWaived: !cardFeeApplied,
  });

  const vrboBlocked = booking?.channel === "vrbo";

  function setRate(i, value) {
    setRates((rs) => rs.map((r, idx) => (idx === i ? { ...r, rate: value } : r)));
  }

  async function onScrape() {
    if (!booking?.checkin_date || !booking?.checkout_date) return;
    setScraping(true);
    setScrapeHint(null);
    const scraped = await scrapeRoomRate(booking.checkin_date, booking.checkout_date);
    setScraping(false);
    if (!scraped || !scraped.length) {
      setScrapeHint("Scrape failed — enter rates manually.");
      return;
    }
    // Merge scraped rates onto existing rows (preserving dates); if lengths
    // differ, rebuild from the scrape output.
    setRates((rs) => {
      if (scraped.length === rs.length) {
        return rs.map((r, i) => ({ date: r.date || rs[i].date, rate: scraped[i]?.rate ?? rs[i].rate }));
      }
      return scraped.map((s) => ({ date: s.date, rate: String(s.rate) }));
    });
  }

  function onFillSame() {
    if (!rates.length) return;
    const first = rates[0].rate;
    setRates((rs) => rs.map((r) => ({ ...r, rate: first })));
  }

  function validate() {
    const e = {};
    if (!rates.length) {
      e.rates = "Add at least one night.";
    } else {
      rates.forEach((r, i) => {
        if (!r.rate || Number(r.rate) <= 0) e[`rate_${i}`] = "Enter a rate > 0";
      });
    }
    const ci = booking?.checkin_date;
    const co = booking?.checkout_date;
    if (!isISODate(ci)) e.checkin_date = "Required";
    if (!isISODate(co)) e.checkout_date = "Required";
    if (ci && co) {
      const n = computeNights(ci, co);
      if (n == null) e.checkout_date = "Check-out must be after check-in";
      else if (!inNightRange(n)) e.nights = `Stays are ${MIN_NIGHTS}-${MAX_NIGHTS} nights`;
    }
    if (!["yes", "no"].includes(wedding)) e.wedding = "Required";
    const pct = Number(discountPct) || 0;
    const subject = discountSubject.trim();
    if (pct > 0 && !subject) e.discountSubject = "Add a label for the discount";
    if (subject && pct <= 0) e.discountPct = "Enter a percentage > 0";
    if (cleaningFee === "" || Number(cleaningFee) < 0) e.cleaningFee = "Enter a cleaning fee";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onApproveSend() {
    if (!validate()) return;
    setSubmitting(true);
    setResult(null);
    const res = await quoteApproveSend({
      booking_id: booking.booking_id,
      token,
      nightly_rates: rates.map((r) => ({ date: r.date, rate: Number(r.rate) || 0 })),
      cleaning_fee: Number(cleaningFee) || 0,
      wedding,
      discount_subject: discountSubject.trim(),
      discount_percentage: Number(discountPct) || 0,
      card_fee_waived: paymentChannel === "card" && !cardFeeApplied,
    });
    setSubmitting(false);
    if (res && res.ok) {
      setResult({ ok: true, total: q.fullAmount, name: `${booking.first_name || ""} ${booking.last_name || ""}`.trim() });
      // Do NOT call onDone() here: app.jsx would flip step to adminDone and
      // unmount this panel before the success card below can render. The
      // success card is the terminal state; a "Done" button there calls onDone.
    } else {
      const err = res?.error || "server_error";
      const msg =
        err === "invalid_token"
          ? "Link invalid — request a new one from your lead alert."
          : err === "vrbo_blocked"
            ? "Quotes are not sent for VRBO bookings."
            : err === "network_error"
              ? "Network error — check your connection and try again."
              : "Something went wrong. Please try again.";
      setResult({ ok: false, error: msg });
    }
  }

  // --- Render states --------------------------------------------------------

  if (loading) {
    return (
      <div class="admin-panel">
        <div class="admin-header">
          <h2>Quote review</h2>
        </div>
        <p class="band">Loading booking…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div class="admin-panel">
        <div class="admin-header">
          <h2>Quote review</h2>
        </div>
        <div class="surface status status-err">{loadError}</div>
      </div>
    );
  }

  if (vrboBlocked) {
    return (
      <div class="admin-panel">
        <div class="admin-header">
          <h2>Quote review</h2>
        </div>
        <div class="surface status status-err">
          Quotes are not sent for VRBO bookings. This guest has already paid through VRBO.
        </div>
      </div>
    );
  }

  // Success card.
  if (result && result.ok) {
    return (
      <div class="admin-panel">
        <div class="admin-header">
          <h2>Quote sent</h2>
        </div>
        <div class="surface status status-ok">
          <p>Quote sent to {result.name || "the guest"}. Total {formatMoney(result.total)}.</p>
          <p class="hint">Reserve link included in the email.</p>
          <label class="field reserve-copy">
            <span class="field-label">Reserve link (copy to share)</span>
            <input type="text" readonly value={reserveLink} onClick={(e) => e.currentTarget.select()} />
          </label>
          <button type="button" class="btn-chip" onClick={() => onDone?.()}>Done</button>
        </div>
      </div>
    );
  }

  // --- Form ---------------------------------------------------------------

  const nights = rates.length;
  const nightsValid = booking?.checkin_date && booking?.checkout_date
    ? computeNights(booking.checkin_date, booking.checkout_date)
    : null;

  return (
    <div class="admin-panel">
      <div class="admin-header">
        <h2>Quote review</h2>
        <p class="band">
          {booking?.first_name} {booking?.last_name} · {booking?.checkin_date} → {booking?.checkout_date}
          {nightsValid ? ` · ${nightsValid} nights` : ""}
        </p>
      </div>

      {result && !result.ok ? <div class="surface status status-err">{result.error}</div> : null}

      {selfBooking ? (
        <div class="surface status status-ok">
          Self-booking test (house email): the bottom line is forced to {formatMoney(2)} —{" "}
          {formatMoney(1)} first + {formatMoney(1)} second installment, card fee waived, whatever
          you enter above. The row carries a {formatMoney(1)} deposit stand-in for the
          card-on-file damage-charge test.
        </div>
      ) : null}

      {/* Per-night rates */}
      <div class="surface">
        <div class="rate-section-head">
          <strong>Nightly rates</strong>
          <div class="chip-row">
            <button type="button" class="btn-chip" onClick={onScrape} disabled={scraping || !booking?.checkin_date}>
              {scraping ? "Scraping…" : "Scrape"}
            </button>
            <button type="button" class="btn-chip" onClick={onFillSame} disabled={!rates.length}>
              Fill same
            </button>
          </div>
        </div>
        {scrapeHint ? <p class="band warn">{scrapeHint}</p> : null}
        {errors.rates ? <p class="band err">{errors.rates}</p> : null}
        <div class="rate-list">
          {rates.map((r, i) => (
            <div class="rate-row" key={r.date + i}>
              <span class="rate-date">{formatOrdinalDate(r.date)}</span>
              <label class="rate-input-wrap">
                <span class="rate-currency">$</span>
                <input
                  class="rate-input"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="1"
                  value={r.rate}
                  onInput={(e) => setRate(i, e.currentTarget.value)}
                  aria-invalid={!!errors[`rate_${i}`]}
                  placeholder="0"
                />
              </label>
            </div>
          ))}
        </div>
        <div class="rate-subtotal">
          <span>Room subtotal</span>
          <span>{formatMoney(q.roomSubtotal)}</span>
        </div>
      </div>

      {/* Fees / tax / discount */}
      <div class="surface">
        <label class="field">
          <span class="field-label">Cleaning fee</span>
          <input
            type="number"
            min="0"
            step="1"
            value={cleaningFee}
            onInput={(e) => setCleaningFee(e.currentTarget.value)}
            aria-invalid={!!errors.cleaningFee}
          />
          {errors.cleaningFee ? <span class="field-error">{errors.cleaningFee}</span> : null}
        </label>

        <label class="field">
          <span class="field-label">Wedding?</span>
          <select value={wedding} onChange={(e) => setWedding(e.currentTarget.value)} aria-invalid={!!errors.wedding}>
            <option value="no">No (no fee)</option>
            <option value="yes">Yes (+{formatMoney(WEDDING_FEE)})</option>
          </select>
        </label>

        <div class="readonly-line">
          <span>Hotel tax (12.5% × room)</span>
          <span>{formatMoney(q.tax)}</span>
        </div>

        <div class="row">
          <label class="field">
            <span class="field-label">Discount subject</span>
            <input
              type="text"
              value={discountSubject}
              onInput={(e) => setDiscountSubject(e.currentTarget.value)}
              placeholder="e.g. Returning guest"
              aria-invalid={!!errors.discountSubject}
            />
            {errors.discountSubject ? <span class="field-error">{errors.discountSubject}</span> : null}
          </label>
          <label class="field">
            <span class="field-label">Discount %</span>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={discountPct}
              onInput={(e) => setDiscountPct(e.currentTarget.value)}
              aria-invalid={!!errors.discountPct}
            />
            {errors.discountPct ? <span class="field-error">{errors.discountPct}</span> : null}
          </label>
        </div>
        {discountPct > 0 ? (
          <div class="readonly-line">
            <span>Discount amount</span>
            <span>-{formatMoney(q.discountAmount)}</span>
          </div>
        ) : null}

        <div class="readonly-line">
          <span>Payment method (from lead)</span>
          <span>{PAYMENT_CHANNEL_LABELS[booking?.payment_channel] || booking?.payment_channel || "—"}</span>
        </div>

        {paymentChannel === "card" && !selfBooking ? (
          <label class="field checkbox-field">
            <input
              type="checkbox"
              checked={cardFeeApplied}
              onChange={(e) => setCardFeeApplied(e.currentTarget.checked)}
            />
            <span class="field-label">Add card processing fee (3.3% + 30¢ per payment) ({formatMoney(qFeeIfShown?.cardFee || 0)}) — untick to waive for this booking</span>
          </label>
        ) : null}
      </div>

      {/* Totals */}
      <div class="surface total-card">
        <div class="readonly-line total-line">
          <span>Total</span>
          <span>{formatMoney(q.fullAmount)}</span>
        </div>
        {shortLead ? (
          <p class="band warn">Short-lead: full payment before booking.</p>
        ) : (
          <p class="band">
            {formatMoney(q.firstRate)} before booking · {formatMoney(q.secondRate)} {SECOND_INSTALLMENT_DAYS} days before arrival
          </p>
        )}
        {errors.nights ? <p class="band warn">{errors.nights}</p> : null}
      </div>

      {/* Live preview */}
      <div class="surface">
        <strong>Preview (guest email)</strong>
        <p class="hint">Reserve link included in the sent email.</p>
        <pre class="preview">{previewText}</pre>
      </div>

      {/* Sticky approve + send */}
      <div class="sticky-bar">
        <button
          type="button"
          class="btn-primary"
          onClick={onApproveSend}
          disabled={submitting || vrboBlocked}
        >
          {submitting ? "Sending…" : `Approve + Send Quote (${formatMoney(q.fullAmount)})`}
        </button>
      </div>
    </div>
  );
}
